import { isAxiosError } from "axios";
import type { EvidenceFileState } from "@/types";

/**
 * 증거 원본 내려받기 실패를 **사실대로** 구분한다.
 *
 * 왜 `apiErrorDetail` 을 쓰지 않는가 — 두 가지가 맞지 않는다:
 *
 * 1. **blob 응답의 오류 본문은 Blob 이다.** `responseType: "blob"` 이면 axios 가
 *    410/404 의 JSON 본문도 Blob 으로 준다. `err.response.data.detail` 은
 *    `undefined` 라, 공용 파서는 `EVIDENCE_LOST` 를 통째로 버리고 일반 문구로
 *    덮는다. 본문을 읽으려면 `await blob.text()` 후 파싱해야 한다.
 * 2. **공용 파서는 410·429 를 모르고, 객체형 detail 도 못 다룬다.** 그래서
 *    소실·미저장·한도초과가 전부 같은 "실패했습니다" 로 뭉개진다 — 이 화면이
 *    존재하는 이유를 정면으로 무너뜨리는 결과다.
 *
 * "없어졌다"와 "처음부터 없었다"와 "지금 받을 수 없다"는 서로 다른 사실이고,
 * 화면은 그 셋을 다르게 말해야 한다.
 */
export type EvidenceFailureCode =
  | "EVIDENCE_LOST"
  | "EVIDENCE_NEVER_STORED"
  | "RATE_LIMITED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "SERVER_ERROR"
  | "UNKNOWN";

export interface EvidenceFailure {
  code: EvidenceFailureCode;
  /** 사용자에게 보여 줄 한국어 문장. */
  message: string;
  /** 서버가 알려 준 상태 (있을 때만). */
  state?: EvidenceFileState;
  /** 소실된 파일의 등록 당시 이름 (있을 때만). */
  fileName?: string;
  /** 소실 시에도 남아 있는 저장 해시 — 나중에 되찾을 근거. */
  storedHash?: string;
}

interface ServerDetail {
  code?: string;
  state?: string;
  message?: string;
  file_name?: string;
  stored_hash?: string;
}

/** blob 이든 객체든 문자열이든, 서버가 준 detail 을 꺼낸다. */
async function readDetail(
  data: unknown,
): Promise<ServerDetail | string | null> {
  if (data == null) return null;

  // responseType:"blob" 경로 — 본문을 텍스트로 되읽어야 detail 이 보인다.
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text) return null;
      const parsed = JSON.parse(text);
      return parsed?.detail ?? null;
    } catch {
      return null;
    }
  }

  if (typeof data === "object" && "detail" in (data as object)) {
    return (data as { detail: ServerDetail | string }).detail ?? null;
  }
  return null;
}

const HAS_HANGUL = /[가-힣]/;

/**
 * 내려받기 실패를 구분된 사실로 바꾼다.
 *
 * 비동기인 이유는 위 1번 — Blob 본문을 읽어야 하기 때문이다.
 */
export async function parseEvidenceFailure(
  err: unknown,
): Promise<EvidenceFailure> {
  if (!isAxiosError(err)) {
    return {
      code: "UNKNOWN",
      message: "원본을 받지 못했습니다. 잠시 후 다시 시도해 주세요.",
    };
  }

  const status = err.response?.status;
  const detail = await readDetail(err.response?.data);
  const obj = detail && typeof detail === "object" ? detail : null;

  if (obj?.code === "EVIDENCE_LOST") {
    return {
      code: "EVIDENCE_LOST",
      state: "lost",
      fileName: obj.file_name ?? undefined,
      storedHash: obj.stored_hash ?? undefined,
      message:
        obj.message && HAS_HANGUL.test(obj.message)
          ? obj.message
          : "원본 파일이 소실되었습니다. 등록 기록은 남아 있으나 저장소에서 파일을 찾을 수 없습니다.",
    };
  }

  if (obj?.code === "EVIDENCE_NEVER_STORED") {
    return {
      code: "EVIDENCE_NEVER_STORED",
      state: "never_stored",
      message:
        obj.message && HAS_HANGUL.test(obj.message)
          ? obj.message
          : "이 시료에는 해당 원본 파일이 저장된 적이 없습니다.",
    };
  }

  if (status === 429) {
    return {
      code: "RATE_LIMITED",
      message:
        "증거 파일 내려받기 한도를 초과했습니다. 잠시 후 다시 시도해 주세요.",
    };
  }
  if (status === 403) {
    return {
      code: "FORBIDDEN",
      message: "이 원본을 내려받을 권한이 없습니다.",
    };
  }
  if (status === 404) {
    return {
      code: "NOT_FOUND",
      message: "시료를 찾을 수 없습니다. 목록을 새로 고친 뒤 다시 시도하세요.",
    };
  }
  if (status && status >= 500) {
    return {
      code: "SERVER_ERROR",
      message:
        "서버에서 원본을 꺼내지 못했습니다. 반복되면 관리자에게 문의하세요.",
    };
  }

  // 문자열 detail 이 한국어면 그대로 쓴다 (공용 파서와 같은 규칙).
  if (typeof detail === "string" && HAS_HANGUL.test(detail)) {
    return { code: "UNKNOWN", message: detail.trim() };
  }
  return {
    code: "UNKNOWN",
    message: "원본을 받지 못했습니다. 잠시 후 다시 시도해 주세요.",
  };
}
